import { useState, useCallback } from "react";
import {
  Box,
  Button,
  TextField,
  Autocomplete,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import axios from "../../../api/axios";
import { useCRUD } from "../../../api/crud";
import { useApiRequest } from "../../../api/axios";
import { EQUIPMENT_MODELS_ENDPOINT, EQUIPMENT_PRODUCERS_ENDPOINT } from "../../../consts";
import { ERROR_SNACKBAR, SUCCESS_SNACKBAR } from "../../../consts";
import styles from "../dynamicForm/dynamicForm.module.css";

export interface EquipmentModelFormProps {
  onSuccess?: (data?: { id: number; name: string; display_name: string; producer?: number }) => void;
}

export default function EquipmentModelForm({ onSuccess }: EquipmentModelFormProps) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { create, loading, error } = useCRUD(EQUIPMENT_MODELS_ENDPOINT);
  const { executeRequest } = useApiRequest();
  const [producerId, setProducerId] = useState<number | null>(null);
  const [name, setName] = useState("");
  const [producerOptions, setProducerOptions] = useState<{ id: number; name: string }[]>([]);
  const [producerLoading, setProducerLoading] = useState(false);

  const fetchProducers = useCallback(
    async (search?: string) => {
      setProducerLoading(true);
      try {
        const params = new URLSearchParams();
        if (search && search.trim()) params.set("search", search.trim());
        const { data } = await executeRequest(() =>
          axios.get(`${EQUIPMENT_PRODUCERS_ENDPOINT}${params.toString() ? "?" + params.toString() : ""}`)
        );
        setProducerOptions(Array.isArray(data?.results) ? data.results : data || []);
      } catch {
        setProducerOptions([]);
      } finally {
        setProducerLoading(false);
      }
    },
    [executeRequest]
  );

  const handleSubmit = async () => {
    if (producerId == null || !name.trim()) {
      enqueueSnackbar(t("Please fill required fields"), ERROR_SNACKBAR);
      return;
    }
    const result = await create({ producer: producerId, name: name.trim() });
    if (result?.data?.id != null) {
      enqueueSnackbar(t("Form was succesfully processed."), SUCCESS_SNACKBAR);
      onSuccess?.(result.data);
    } else {
      const msg =
        (typeof result?.errorData === "string" ? result.errorData : result?.errorData?.message) ||
        t("There was an error, please review the form.");
      enqueueSnackbar(msg, ERROR_SNACKBAR);
    }
  };

  const errorMessage =
    error && (typeof error === "string" ? error : (error as { message?: string })?.message);

  const selectedProducer = producerOptions.find((p) => p.id === producerId) || null;

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
      <Box
        sx={{
          flex: { xs: "1 1 100%", sm: "1 1 100%" },
          maxWidth: { xs: "100%", sm: "100%" },
        }}
      >
        <Autocomplete
          size="small"
          options={producerOptions}
          value={selectedProducer}
          getOptionLabel={(opt) => (typeof opt === "string" ? opt : opt?.name ?? "")}
          isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
          loading={producerLoading}
          onOpen={() => fetchProducers()}
          onInputChange={(_, value) => fetchProducers(value)}
          onChange={(_, newVal) => setProducerId(newVal?.id ?? null)}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("Producer")}
              required
              fullWidth
            />
          )}
          noOptionsText={t("No options")}
        />
        {errorMessage && <div className={styles.errorMessage}>{errorMessage}</div>}
      </Box>
      <Box
        sx={{
          flex: { xs: "1 1 100%", sm: "1 1 100%" },
          maxWidth: { xs: "100%", sm: "100%" },
        }}
      >
        <TextField
          size="small"
          label={t("Name")}
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          fullWidth
        />
      </Box>
      <Box sx={{ display: "flex", width: "100%", justifyContent: "flex-end" }}>
        <Button variant="contained" color="success" disabled={loading} onClick={handleSubmit}>
          {t("Submit")}
        </Button>
      </Box>
    </Box>
  );
}
